import React from "react";

// Shared color constants
export const notificationColors = {
  white: "#FFFFFF",
  black: "#000000",
  primary: "#0055A4",
  gray50: "#F9FAFB",
  gray200: "#E4E7EC",
  gray400: "#98A2B3",
  gray500: "#667085",
  gray600: "#475467",
  gray900: "#101828",
  green: "#12B76A",
  red: "#DC2626",
  lightBlue: "#E0EFFF",
};

// Shared notification panel styles (plain objects for reusability)
export const sharedNotificationStyles = {
  // Container
  webNotificationPanel: {
    position: "relative" as const,
    width: "100%",
    maxWidth: "480px",
    backgroundColor: notificationColors.white,
    borderRadius: "12px",
    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
    overflow: "hidden" as const,
  },

  // Bell icon container
  bellIconContainer: {
    position: "absolute" as const,
    top: "-12px",
    right: "24px",
    width: "48px",
    height: "48px",
    backgroundColor: notificationColors.lightBlue,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center" as const,
    justifyContent: "center" as const,
    zIndex: 10,
  },

  // Bell badge
  bellBadge: {
    position: "absolute" as const,
    top: "4px",
    right: "4px",
    width: "20px",
    height: "20px",
    backgroundColor: notificationColors.red,
    borderRadius: "50%",
    color: notificationColors.white,
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "Manrope, sans-serif",
    display: "flex",
    alignItems: "center" as const,
    justifyContent: "center" as const,
    border: `2px solid ${notificationColors.white}`,
  },

  // Panel content
  notificationsPanelContent: {
    padding: "24px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },

  // Header
  webPanelHeader: {
    display: "flex",
    justifyContent: "space-between" as const,
    alignItems: "center" as const,
    paddingBottom: "16px",
    borderBottom: `1px solid ${notificationColors.gray200}`,
  },

  // Header title
  webPanelTitle: {
    fontFamily: "Manrope, sans-serif",
    fontSize: "18px",
    fontWeight: 700,
    color: notificationColors.gray900,
  },

  // Unread badge
  unreadBadge: {
    backgroundColor: notificationColors.green,
    color: notificationColors.white,
    borderRadius: "10px",
    padding: "2px 8px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "Manrope, sans-serif",
    lineHeight: "16px",
  },

  // Mark all read link
  markAllRead: {
    color: notificationColors.primary,
    fontSize: "14px",
    fontWeight: 400,
    fontFamily: "Manrope, sans-serif",
    cursor: "pointer",
  },

  // Notification item
  webNotificationItem: {
    display: "flex",
    gap: "12px",
    padding: "12px",
    backgroundColor: notificationColors.gray50,
    borderRadius: "8px",
    alignItems: "flex-start" as const,
  },

  // Notification dot
  webNotificationDot: {
    width: "8px",
    height: "8px",
    backgroundColor: notificationColors.primary,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: "6px",
  },

  // Notification title
  webNotificationTitle: {
    fontFamily: "Manrope, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: notificationColors.gray900,
    lineHeight: "20px",
    marginBottom: "4px",
    overflowWrap: "anywhere" as const,
  },

  // Notification content
  webNotificationContent: {
    fontFamily: "Manrope, sans-serif",
    fontSize: "13px",
    fontWeight: 400,
    color: notificationColors.gray600,
    lineHeight: "18px",
    marginBottom: "8px",
    wordBreak: "break-word" as const,
    overflowWrap: "anywhere" as const,
  },

  // Notification time
  webNotificationTime: {
    fontFamily: "Manrope, sans-serif",
    fontSize: "12px",
    fontWeight: 400,
    color: notificationColors.gray400,
    lineHeight: "16px",
  },

  // Close button (for toast)
  closeButton: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    cursor: "pointer",
    color: notificationColors.gray500,
  },

  // See all notifications
  seeAllNotifications: {
    textAlign: "center" as const,
    color: notificationColors.primary,
    fontSize: "14px",
    fontWeight: 600,
    fontFamily: "Manrope, sans-serif",
    cursor: "pointer",
    paddingTop: "8px",
    borderTop: `1px solid ${notificationColors.gray200}`,
  },

  pushNotificationCard: {
    padding: "16px 20px",
    width: "100%",
    maxWidth: "480px",
    borderLeft: `4px solid ${notificationColors.primary}`,
    borderRadius: "8px",
    backgroundColor: notificationColors.lightBlue,
    overflow: "hidden" as const,
    boxSizing: "border-box" as const,
  },
  pushNotificationCardContent: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    width: "100%",
  },
  pushNotificationCardText: {
    flex: 1,
    minWidth: 0,
  },
  pushNotificationCardMeta: {
    fontFamily: "Manrope, sans-serif",
    fontSize: "12px",
    fontWeight: 400,
    color: notificationColors.gray600,
    lineHeight: "16px",
    marginBottom: "4px",
  },
  pushNotificationCardTitle: {
    fontFamily: "Manrope, sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    color: notificationColors.gray900,
    lineHeight: "20px",
    marginBottom: "4px",
    overflowWrap: "anywhere" as const,
  },
  pushNotificationCardBody: {
    fontFamily: "Manrope, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: notificationColors.gray600,
    lineHeight: "20px",
    wordBreak: "break-word" as const,
    overflowWrap: "anywhere" as const,
  },
  pushNotificationCardCloseButton: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
    cursor: "pointer",
    color: notificationColors.gray500,
    marginTop: "2px",
  },
  pushNotificationCardEmptyState: {
    padding: "24px 20px",
    fontFamily: "Manrope, sans-serif",
    fontSize: "14px",
    color: notificationColors.gray500,
    textAlign: "center" as const,
    lineHeight: "20px",
  },
};

export const BellIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={style}>
    <path
      d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
      fill={notificationColors.primary}
    />
  </svg>
);

export const CloseIcon: React.FC<{ style?: React.CSSProperties; onClick?: () => void }> = ({ 
  style, 
  onClick 
}) => (
  <svg 
    width="16" 
    height="16" 
    viewBox="0 0 16 16" 
    fill="none" 
    style={{ cursor: "pointer", ...style }}
    onClick={onClick}
  >
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
